import React, { useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import SortableTable from './SortableTable';
import { useNavigate } from 'react-router-dom';
import { APIURL } from '../config';
import Loading from './loading';
import Snackbar from './Snackbar';

const AdminRoute = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [tokenReady, setTokenReady] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'info' });
  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  // Perform token exchange — sends the MSAL ID token to the backend for validation
  const performTokenExchange = useCallback(async (msalIdToken) => {
    try {
      const tokenRes = await fetch(APIURL + '/token-exchange', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${msalIdToken}`,
        },
        body: JSON.stringify({}),
      });
      const tokenData = await tokenRes.json();
      if (tokenData.token) {
        localStorage.setItem('authToken', tokenData.token);
        localStorage.setItem('userRole', tokenData.role || 'admin');
        setTokenReady(true);
        setTokenError('');
        return true;
      } else {
        setTokenError('Token exchange failed. Please try logging in again.');
        setTokenReady(false);
        return false;
      }
    } catch (err) {
      console.error('Token exchange failed:', err);
      setTokenError('Token exchange failed. Please try logging in again.');
      setSnackbar({ open: true, message: 'Token exchange failed. Please try logging in again.', type: 'error' });
      setTokenReady(false);
      return false;
    }
  }, []);

  // On mount / when MSAL auth state changes: ensure we have a valid JWT
  useEffect(() => {
    const ensureToken = async () => {
      const loginType = localStorage.getItem("loginType");
      if (!isAuthenticated || loginType !== "microsoft") {
        setTokenReady(false);
        return;
      }

      // Check if we already have a token
      const existingToken = localStorage.getItem('authToken');
      if (existingToken) {
        // Verify the token isn't expired by decoding the payload
        try {
          const payload = JSON.parse(atob(existingToken.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp > now) {
            setTokenReady(true);
            return; // Token is still valid
          }
        } catch {
          // Token is malformed, proceed to re-exchange
        }
      }

      // Token missing or expired — acquire MSAL token and perform exchange
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          const silentResult = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          await performTokenExchange(silentResult.idToken);
        } catch (silentErr) {
          console.error('Silent token acquisition failed:', silentErr);
          setTokenError('Session expired. Please log in again.');
          setTokenReady(false);
        }
      } else {
        setTokenError('No Microsoft account found. Please log in again.');
        setTokenReady(false);
      }
    };

    ensureToken();
  }, [isAuthenticated, instance, performTokenExchange]);

  const handleLogin = () => {
    setTokenError('');
    instance.loginPopup(loginRequest)
      .then(async (response) => {
        localStorage.setItem("userInfo", JSON.stringify(response.account));
        localStorage.setItem("loginType", "microsoft");

        // Exchange MSAL ID token for local JWT — wait for completion before navigating
        const success = await performTokenExchange(response.idToken);

        if (success) {
          navigate("/admin", { replace: true });
        }
        // If token exchange failed, tokenError state will show the error message
      })
      .catch(e => {
        setTokenError('Microsoft login failed. Please try again.');
        setSnackbar({ open: true, message: 'Microsoft login failed. Please try again.', type: 'error' });
      });
  };

  const loginType = localStorage.getItem("loginType");

  // Block normal (traditional) login users — only SSO (Microsoft) users can access admin
  if (loginType === 'normal') {
    return (
      <div className="text-center mt-5">
        <h3>Access denied. Admin panel is only available for SSO users.</h3>
        <button className="submit-button" onClick={() => navigate('/home')}>
          Go to Home
        </button>
      </div>
    );
  }

  // Not authenticated via SSO yet — show login button
  if (!isAuthenticated || loginType !== "microsoft") {
    return (
      <div className="text-center mt-5">
        <h3>You must be logged in with Microsoft to access the admin panel.</h3>
        <button className="submit-button" onClick={handleLogin}>
          Login with Microsoft
        </button>
        {tokenError && <p style={{ color: 'red', marginTop: '10px' }}>{tokenError}</p>}
      </div>
    );
  }

  // Show error if token exchange failed for an authenticated user
  if (tokenError) {
    return (
      <div className="text-center mt-5">
        <h3 style={{ color: 'red' }}>{tokenError}</h3>
        <button className="submit-button" onClick={handleLogin}>
          Retry Login
        </button>
      </div>
    );
  }

  // Wait for token to be ready before rendering SortableTable
  if (!tokenReady) {
    return <Loading />;
  }

  return (
    <>
      <Snackbar message={snackbar.message} type={snackbar.type} open={snackbar.open} onClose={closeSnackbar} />
      <SortableTable />
    </>
  );
};

export default AdminRoute;